import React, { FC, useEffect, ReactNode } from 'react';
import { GiFiles } from 'react-icons/gi';
import { useQueryClient, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FaCheckDouble, FaFileSignature, FaDownload } from 'react-icons/fa';
import TooltipWrapper from 'components/TooltipWrapper';

import styled from 'styled-components';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';
import { usePermissionsContext } from 'store/permissions';
import { useActiveClientContext } from 'store/active-client';
import { useSelectableTableContext } from 'store/selectable-table';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';

import Button from 'components/Button';
import MainLayout from 'components/main-layout/MainLayout';
import TabNavigation from 'components/TabNavigation';
import LayoutContent from 'components/main-layout/LayoutContent';

import { multiSignDocAsEmployer } from 'utils/helpers/helpers';
import { requestMultipleDownload, downloadMultipleFiles, extractErrorMessage } from 'utils/helpers/archives';

interface IProps {
  isLoading?: boolean;
  error?: unknown;
  canMultiSign?: boolean;
  customSelectableProps?: string[];
  setCustomSelectableRowProps?: (val: string[]) => void;
  setMultiActionActive?: (active: boolean) => void;
  children: React.ReactNode;
}

interface TabCount {
  receivedCount: number;
  forSignCount: number;
  sentCount: number;
}

interface Tab {
  to?: string;
  label?: string;
  entityName?: string;
  onClick?: () => void;
  onRefresh?: () => void;
  notification?: ReactNode;
}

const InnerWrapper: FC<IProps> = ({
  isLoading,
  children,
  error,
  canMultiSign,
  customSelectableProps,
  setCustomSelectableRowProps,
  setMultiActionActive,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();

  const { showSelectColumn, setShowSelectColumn, resetSelectable, selectedRowsOnAction, selectAllRows } =
    useSelectableTableContext();

  useEffect(() => {
    /**
     * If we go between different tabs and then go back
     * to sign tab - reset active table select state.
     */
    resetSelectable();
    if (setMultiActionActive) {
      setMultiActionActive(false);
    }
  }, []);

  const refreshDocuments = () => {
    const { pathname } = location;
    const currentTab = tabs.find(tab => buildClientUrl(tab.to!, client) === pathname);
    if (currentTab) {
      queryClient.resetQueries(currentTab.entityName);
      queryClient.invalidateQueries('tabCounts');
    }
  };

  // Fetch tab counts
  const { data: tabCounts } = useQuery<TabCount>('tabCounts', async () => {
    const response = await API.get('/document-holders/tab-counts');
    return response.data;
  });

  // Used for documents count in tabs
  const getTabNotification = (count?: number) => {
    if (count && count > 0) {
      const displayCount = count > 99 ? '99+' : count;
      return <NotificationBadge>{displayCount}</NotificationBadge>;
    }
    return null;
  };

  /**
   * !!!!!!!!!!!!!!!!!!!!!!!!
   *
   * If updating the tabs logic here, please make sure
   * that you update my-documents tabs as well
   *
   * !!!!!!!!!!!!!!!!!!!!!!!!
   */
  const tabs: Tab[] = [
    {
      to: '/documents/received',
      label: t('documents.received'),
      entityName: buildClientEntity('documents-received', client),
      notification: getTabNotification(tabCounts?.receivedCount),
    },
    {
      to: '/documents/ready-for-sign-and-send',
      label: t('documents.awaiting'),
      entityName: buildClientEntity('documents-for-sign', client),
      notification: getTabNotification(tabCounts?.forSignCount),
    },
    {
      to: '/documents/sent',
      label: t('documents.sent'),
      entityName: buildClientEntity('documents-sent', client),
      notification: getTabNotification(tabCounts?.sentCount),
    },
    {
      to: '/documents/archived',
      label: t('documents.archived'),
      entityName: buildClientEntity('documents-archived', client),
    },
    {
      to: '/documents/trash',
      label: t('documents.trash'),
      entityName: buildClientEntity('documents-trash', client),
    },
    {
      onRefresh: refreshDocuments,
    },
  ].map(tab => {
    if (tab.entityName)
      return {
        ...tab,
        onClick: () => {
          queryClient.resetQueries(tab.entityName);
          history.push(buildClientUrl(tab.to, client));
        },
      };

    return tab;
  });

  const showNoSelectedMsg = () =>
    showModal({
      type: 'error',
      content: t('documents.table.noDocsSelected'),
    });

  const multiSend = (docIds: number[]) => {
    return API.post('document-holders/send-multiple', {
      document_holder_ids: docIds,
    })
      .then(res => {
        const { message } = res.data;
        if (message) {
          const { body } = message;

          showModal({
            type: 'success',
            content: body,
          });
        }
        queryClient.resetQueries(buildClientEntity('documents-for-sign', client));
        queryClient.resetQueries(buildClientEntity('documents-received', client));
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      )
      .finally(() => resetSelectable());
  };

  const multiApprove = (docIds: number[]) => {
    return API.post('document-holders/approve-multiple', {
      document_holder_ids: docIds,
    })
      .then(res => {
        const { message } = res.data;
        if (message) {
          const { type, body } = message;
          if (type === 'multi_approve')
            showModal({
              type: 'success',
              content: body,
            });
        }
        queryClient.resetQueries(buildClientEntity('documents-for-sign', client));
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      )
      .finally(() => resetSelectable());
  };

  const multiSign = (rowIds: number[]) => {
    showModal({
      type: 'loading',
      heading: t('documents.eSign'),
      content: t('documents.eSignSubtitle'),
    });
    multiSignDocAsEmployer(
      rowIds,
      showModal,
      () => {
        queryClient.resetQueries(buildClientEntity('documents-for-sign', client));
        resetSelectable();
      },
      'success message',
      t
    );
  };

  const confirmSendFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      showModal({
        type: 'confirm',
        content: t('documents.table.confirmDocsAware_plural'),
        onCancel: () => {
          resetSelectable();
          if (setMultiActionActive) {
            setMultiActionActive(false);
          }
        },
        onConfirm: () =>
          showModal({
            type: 'confirm',
            content: t('documents.table.confirmSendDocMsg_plural'),
            onCancel: () => {
              resetSelectable();
              if (setMultiActionActive) {
                setMultiActionActive(false);
              }
            },
            confirmButton: (
              <Button
                className="confirm-btn"
                btnType="primary"
                onClick={() => {
                  hideModal();
                  multiSend(rowIds);
                  if (setMultiActionActive) {
                    setMultiActionActive(false);
                  }
                }}
              >
                {t('common.confirm')}
              </Button>
            ),
          }),
      });
    } else {
      showNoSelectedMsg();
    }
  };

  const confirmApproveFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      showModal({
        type: 'confirm',
        content: t('documents.table.confirmDocsAware_plural'),
        onCancel: () => {
          resetSelectable();
          if (setMultiActionActive) {
            setMultiActionActive(false);
          }
        },
        onConfirm: () =>
          showModal({
            type: 'confirm',
            content: t('documents.table.confirmApproveDocMsg_plural'),
            onCancel: () => {
              resetSelectable();
              if (setMultiActionActive) {
                setMultiActionActive(false);
              }
            },
            confirmButton: (
              <Button
                className="confirm-btn"
                btnType="primary"
                onClick={() => {
                  hideModal();
                  multiApprove(rowIds);
                  if (setMultiActionActive) {
                    setMultiActionActive(false);
                  }
                }}
              >
                {t('common.confirm')}
              </Button>
            ),
          }),
      });
    } else {
      showNoSelectedMsg();
    }
  };

  const confirmSignFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      showModal({
        type: 'confirm',
        content: t('documents.table.confirmDocsAware_plural'),
        onCancel: () => {
          resetSelectable();
          if (setMultiActionActive) {
            setMultiActionActive(false);
          }
        },
        onConfirm: () =>
          showModal({
            type: 'confirm',
            content: t('documents.table.confirmSignDocMsg_plural'),
            onCancel: () => {
              resetSelectable();
              if (setMultiActionActive) {
                setMultiActionActive(false);
              }
            },
            confirmButton: (
              <Button
                className="confirm-btn"
                btnType="primary"
                onClick={() => {
                  hideModal();
                  multiSign(rowIds);
                  if (setMultiActionActive) {
                    setMultiActionActive(false);
                  }
                }}
              >
                {t('common.confirm')}
              </Button>
            ),
          }),
      });
    } else {
      showNoSelectedMsg();
    }
  };

  const confirmBulkDownloadFlow = async (rowIds: number[]) => {
    if (rowIds.length === 0) {
      showNoSelectedMsg();
      return;
    }

    const currentTab = 'documents-tab';

    try {
      showModal({
        type: 'loading',
        heading: t('documents.bulkDownload.preparing'),
        content: t('documents.bulkDownload.pleaseWait'),
      });

      const response = await requestMultipleDownload(rowIds, currentTab);

      if (response.type === 'immediate-download') {
        hideModal();
        showModal({
          type: 'success',
          content: t('documents.bulkDownload.immediateDownloadMessage'),
        });

        downloadMultipleFiles(response.links);

        resetSelectable();
        if (setMultiActionActive) {
          setMultiActionActive(false);
        }
      } else {
        hideModal();
        showModal({
          type: 'success',
          heading: t('documents.bulkDownload.archiveGenerating'),
          content: t('documents.bulkDownload.archiveMessage'),
        });

        resetSelectable();
        if (setMultiActionActive) {
          setMultiActionActive(false);
        }
      }
    } catch (err: any) {
      hideModal();
      const errorMessage = extractErrorMessage(err, t('documents.bulkDownload.error'));
      showModal({
        type: 'error',
        content: errorMessage,
      });
    }
  };

  return (
    <MainLayout title={t('documents.title')}>
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
        <div>
          <TabNavigation tabs={tabs} />
        </div>
        {canMultiSign && !showSelectColumn && (
          <MultiActionWrapper>
            <StyledH5>{t('documents.table.multiActions')}:</StyledH5>

            <ButtonsWrapper>
              {checkPermission(PermissionKeys.CAN_MULTI_APPROVE) && (
                <Button
                  className="ml-2 mb-3"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_APPROVE]);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <FaCheckDouble className="mr-2" />
                  {t('documents.table.approval')}
                </Button>
              )}
              {checkPermission(PermissionKeys.CAN_MULTI_SIGN) && (
                <Button
                  className="ml-2 mb-3"
                  btnType="info"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_SIGN]);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <FaFileSignature className="mr-2" />
                  {t('documents.table.signing')}
                </Button>
              )}
              {checkPermission(PermissionKeys.CAN_MULTI_SEND) && (
                <Button
                  className="ml-2 mb-3"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_SEND]);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <TooltipWrapper
                    id="tooltip-multisend"
                    style={{ width: '100%' }}
                    overlay={t('documents.table.sendAndOrArchive')}
                  >
                    <GiFiles
                      size={18}
                      className="mr-2"
                    />
                    {t('documents.table.confirmSendDocHeading')}
                  </TooltipWrapper>
                </Button>
              )}
              <Button
                className="ml-2 mb-3"
                btnType="secondary"
                onClick={() => {
                  if (setCustomSelectableRowProps) setCustomSelectableRowProps(['CAN_MULTI_DOWNLOAD']);
                  setShowSelectColumn(true);
                  if (setMultiActionActive) {
                    setMultiActionActive(true);
                  }
                }}
              >
                <TooltipWrapper
                  id="tooltip-mass-download"
                  style={{ width: '100%' }}
                  overlay={t('documents.bulkDownload.tooltip')}
                >
                  <FaDownload className="mr-2" />
                  {t('documents.table.download')}
                </TooltipWrapper>
              </Button>
            </ButtonsWrapper>
          </MultiActionWrapper>
        )}
        {canMultiSign && showSelectColumn && (
          <div className="mb-3">
            {customSelectableProps?.includes(PermissionKeys.CAN_MULTI_SEND) && (
              <Button
                className="ml-2"
                btnType="primary"
                onClick={() => selectedRowsOnAction(confirmSendFlow)}
                preventDebounce
              >
                <GiFiles
                  size={18}
                  className="mr-2"
                />
                {t('documents.table.send')}
              </Button>
            )}
            {customSelectableProps?.includes(PermissionKeys.CAN_MULTI_APPROVE) && (
              <Button
                className="ml-2"
                btnType="primary"
                onClick={() => selectedRowsOnAction(confirmApproveFlow)}
                preventDebounce
              >
                <FaCheckDouble
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.approve')}
              </Button>
            )}
            {customSelectableProps?.includes(PermissionKeys.CAN_MULTI_SIGN) && (
              <Button
                className="ml-2"
                btnType="info"
                onClick={() => selectedRowsOnAction(confirmSignFlow)}
                preventDebounce
              >
                <FaFileSignature
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.sign')}
              </Button>
            )}
            {customSelectableProps?.includes('CAN_MULTI_DOWNLOAD') && (
              <Button
                className="ml-2"
                btnType="secondary"
                onClick={() => selectedRowsOnAction(confirmBulkDownloadFlow)}
                preventDebounce
              >
                <FaDownload
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.download')}
              </Button>
            )}
            <Button
              className="ml-2"
              btnType="light"
              outlined
              onClick={() => {
                resetSelectable();
                if (setMultiActionActive) {
                  setMultiActionActive(false);
                }
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        )}
      </div>
      <LayoutContent
        isLoading={isLoading}
        error={error}
      >
        {children}
      </LayoutContent>
    </MainLayout>
  );
};

export default InnerWrapper;

const MultiActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  justify-content: flex-end;

  button {
    margin-left: 0 !important;
    margin-bottom: 0 !important;
    white-space: nowrap;
  }

  @media ${props => props.theme.breakpoints.md} {
    flex-wrap: wrap;

    button {
      flex: 1 1 auto;
      min-width: 150px;
    }
  }

  @media ${props => props.theme.breakpoints.sm} {
    button {
      flex: 1 1 100%;
    }
  }
`;

const StyledH5 = styled.h5`
  text-align: right;
  padding-right: 5px;
  position: relative;
  margin: 0 0 0.5rem 0;
  white-space: nowrap;
  @media ${props => props.theme.breakpoints.md} {
    text-align: center;
  }
`;

const NotificationBadge = styled.span`
  background-color: var(--primary);
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-size: 0.8em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -12px;
  right: -20px;
  white-space: nowrap;

  @media ${props => props.theme.breakpoints.sm} {
    top: -10px;
    right: -15px;
    width: 20px;
    height: 20px;
    font-size: 0.7em;
  }
`;
